/**
 * Клиент нашего API.
 *
 * Единственное место в приложении, где встречается fetch: пакетам сеть
 * запрещена, чтобы их можно было унести в другой проект.
 */

export interface Me {
  id: number
  firstName: string
  username: string | null
  language: string
  isPremium: boolean
  timezone: string | null
  isNew: boolean
  /** Внутренняя валюта. Всегда сумма записей журнала — см. pkg/ledger. */
  balance: number
  settings: {
    theme: string
    dailyCardEnabled: boolean
    followupsEnabled: boolean
  }
  profile: {
    displayName: string | null
    gender: string | null
    occupation: string | null
    goals: string[]
  } | null
}

export interface Card {
  slug: string
  name: string
  arcana: 'major' | 'minor'
  suit: 'wands' | 'cups' | 'swords' | 'pentacles' | null
  rank: string | null
  number: number | null
  image: string
  description: string
  upright: string
  reversed: string
}

export interface Deck {
  deck: { slug: string; title: string }
  cards: Card[]
}

export interface Daily {
  date: string
  card: Card
  isReversed: boolean
  revealed: boolean
  note: string | null
}

/** Место в раскладе. Позиции приходят с сервера — фронт их не придумывает. */
export interface SpreadPosition {
  idx: number
  title: string
  short: string
  /** Доли габарита раскладки, не пиксели: экраны разные. */
  x: number
  y: number
  rotate: number
}

export interface Spread {
  slug: string
  title: string
  subtitle: string
  description: string
  topic: string
  cardCount: number
  difficulty: number
  /** Цена во внутренней валюте. Ноль — бесплатно. */
  price: number
  positions: SpreadPosition[]
}

export interface ReadingCard {
  position: SpreadPosition
  slug: string
  name: string
  image: string
  isReversed: boolean
  meaning: string
  description: string
  /** Какое место веера выбрал человек. Нужно для проверки честности. */
  fanSlot: number
  drawOrder: number
}

export interface Reading {
  id: string
  spread: Spread
  question: string | null
  status: 'draft' | 'drawn' | 'generating' | 'ready' | 'failed' | 'refunded'
  cards: ReadingCard[]
  /** Обязательство: выдаётся ДО тасовки. См. pkg/shuffle. */
  serverSeedHash: string
  clientSeed?: string
  nonce: number
  createdAt: string
  revealedAt: string | null
}

/** Раздача возвращает ещё и баланс: за расклад только что списали. */
export interface DrawnReading extends Reading {
  balance: number
}

export interface Proof {
  algorithm: string
  serverSeed: string
  serverSeedHash: string
  clientSeed: string
  nonce: number
  /** Канонический порядок колоды — без него числа не с чем сопоставить. */
  deckOrder: string[]
  cards: Array<{ fanSlot: number; deckIdx: number; slug: string; name: string; reversed: boolean }>
  valid: boolean
}

const HEADER = 'X-Telegram-Init-Data'

/**
 * Ошибка API с кодом.
 *
 * Код нужен потому, что на «не хватает валюты» показывается не текст,
 * а способ её получить, и различать это по строке сообщения нельзя.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, initData: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { [HEADER]: initData, ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers },
  })

  if (!res.ok) {
    // Сообщения разные не для красоты: 401 снаружи Telegram — это норма,
    // а 500 — повод показать другой текст и не предлагать «попробуй ещё раз».
    if (res.status === 401) {
      throw new ApiError('unauthorized', 'Telegram не подтвердил, кто ты. Попробуй открыть приложение заново.', 401)
    }

    // Тело читается как текст: у 502 от прокси его вообще нет, а у 500
    // оно есть, но это не наш JSON.
    const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null
    throw new ApiError(
      body?.error ?? 'unknown',
      body?.message ?? `Что-то пошло не так (${res.status}).`,
      res.status,
    )
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  me: (initData: string) => request<Me>('/api/me', initData),

  setTimezone: (initData: string, timezone: string) =>
    request<void>('/api/me/timezone', initData, {
      method: 'PUT',
      body: JSON.stringify({ timezone }),
    }),

  daily: (initData: string) => request<Daily>('/api/daily', initData),

  revealDaily: (initData: string) =>
    request<void>('/api/daily/reveal', initData, { method: 'POST' }),

  deck: (initData: string) => request<Deck>('/api/cards', initData),

  spreads: (initData: string) =>
    request<{ spreads: Spread[] }>('/api/spreads', initData).then((r) => r.spreads),

  /**
   * Шаг 1 честной тасовки: обязательство.
   *
   * Возвращает serverSeedHash — порядок карт уже зафиксирован, но никому
   * не известен. Списания на этом шаге нет: человек может передумать.
   */
  createReading: (initData: string, spread: string, question: string) =>
    request<Reading>('/api/readings', initData, {
      method: 'POST',
      body: JSON.stringify({ spread, question }),
    }),

  /**
   * Шаг 2: раздача.
   *
   * clientSeed — дайджест жеста, picks — выбранные места веера по порядку
   * выбора. Без жеста сервер откажет: расклад, где он выбрал всё сам, —
   * ровно та бутафория, от которой мы уходим.
   */
  drawReading: (initData: string, id: string, clientSeed: string, picks: number[]) =>
    request<DrawnReading>(`/api/readings/${id}/draw`, initData, {
      method: 'POST',
      body: JSON.stringify({ clientSeed, picks }),
    }),

  reading: (initData: string, id: string) => request<Reading>(`/api/readings/${id}`, initData),

  /** Шаг 3: доказательство. Доступно только после раздачи. */
  verifyReading: (initData: string, id: string) =>
    request<Proof>(`/api/readings/${id}/verify`, initData),
}
